import React, { useRef, useState, useEffect, MutableRefObject } from "react";
import { useToasts } from "@utils/hooks/useToasts";
import { MODALS_ENUM } from "@constants/Modals";
import { ROUTES_E } from "@utils/RoutsName";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { show } from "redux-modal";
import { logon } from "@store/auth/actions";
import useMediaQuery from "@utils/hooks/utils";
import { ErrorType } from "@components/global/new/Error/Error";
import { RootDispatch, RootState } from "@store";
import { IS_DEBUG } from "../../../config";
import AUTHSTATES from "@store/auth/AuthStates";
import useApiRequests from "@services/http/useApiRequest";
import { LoginData } from "@services/http/auth/dto";
import { LogonRequest, RegisterRequest } from "@services/http/auth";
import { makeNotifs } from "@utils/makeNotifs";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import { isTokenAlive } from "@store/auth/cookieStorage";

interface AuthorizationElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface RegistrationElements extends AuthorizationElements {
  repeat: HTMLInputElement;
}

interface LoginForm extends HTMLFormElement {
  readonly elements: AuthorizationElements;
}

interface RegistrationForm extends HTMLFormElement {
  readonly elements: RegistrationElements;
}

interface LoginControl {
  readonly isMob: boolean;
  readonly recaptchaResponse: string | undefined | null;
  readonly terms: boolean;
  readonly isSign: boolean;
  readonly errors?: ErrorType;
  readonly loading: boolean;

  //ref
  reCapRefLogin: any;
  reCapRefRegister: any;

  readonly registrationRef: MutableRefObject<HTMLDivElement | null>;
  readonly loginRef: MutableRefObject<HTMLDivElement | null>;

  changeTerms(): void;
  login(e: React.FormEvent<LoginForm>): void;
  create(e: React.FormEvent<RegistrationForm>): void;
  openForgot(): void;
  openSignup(): void;
  changeWindow(): void;
  setRecaptchaResponse(r: string | undefined): void;
}

export const useLoginControl = (): LoginControl => {
  const auth = useSelector((state: RootState) => state.auth); //useAuth();
  const { ok } = makeNotifs();
  const { success } = useToasts();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch() as RootDispatch;
  const isMob = useMediaQuery("max", 375);
  const [terms, setTerms] = useState<boolean>(false);
  const [errors, setErrors] = useState<ErrorType>();
  const [recaptchaResponse, setRecaptchaResponse] = useState<
    string | undefined
  >();
  const reCapRefLogin = useRef<any>(null);
  const reCapRefRegister = useRef<any>(null);
  const registrationRef = useRef<HTMLDivElement | null>(null);
  const loginRef = useRef<HTMLDivElement | null>(null);

  const requests = useApiRequests();

  const changeTerms = () => setTerms(!terms);
  const changeWindow = () => navigate(ROUTES_E.LOGIN);

  const isSign = search.replace("?", "") === "signup";

  useEffect(() => {
    if (isTokenAlive() && auth.state === AUTHSTATES.AUTHORIZED) {
      navigate(ROUTES_E.MACHINES);
    }
  }, [auth, navigate]);

  const checkNoErrors = (e: React.FormEvent<LoginForm>): boolean => {
    const email = e.currentTarget.elements.email.value;
    const pass = e.currentTarget.elements.password.value;
    let err: ErrorType | undefined = undefined;
    if (!email || !pass) err = { fill: true };
    if (pass.length < 6) err = { length: true };
    setErrors(err);
    return !!!err;
  };

  const authorization = async (e: React.FormEvent<LoginForm>) => {
    e.preventDefault();
    //const recaptcha = IS_DEBUG ? "fake-recaptcha" : recaptchaResponse;
    const elems = e.currentTarget.elements;
    const email = elems.email.value.replace(/\s+/g, "");
    const password = elems.password.value;

    let err: ErrorType | undefined = undefined;

    if (!email || !password) err = { fill: true };
    if (password.length < 6) err = { length: true };

    setErrors(err);

    if (!!!err /*&& recaptcha*/) {
      const data = await requests.enqueue<RecordResponse<LoginData>>(
        new LogonRequest({
          email,
          password,
          //recaptcha: recaptchaResponse,
        })
      );
      if (!data) return;
      //console.log("after data = undefined");
      // if (!IS_DEBUG) {
      //   reCapRefLogin.current.reset();
      // }
      dispatch(logon(data.Record));
      navigate(ROUTES_E.MACHINES);
    }
  };

  const registration = async (e: React.FormEvent<RegistrationForm>) => {
    e.preventDefault();
    //const recaptcha = IS_DEBUG ? "fake-recaptcha" : recaptchaResponse;
    const elems = e.currentTarget.elements;
    const email = elems.email.value.replace(/\s+/g, "");
    const password = elems.password.value;
    const repeat = elems.repeat.value;
    let err: ErrorType | undefined = undefined;

    if (!terms) err = { terms: true };
    if (password !== repeat) err = { ...err, match: true };
    if (!email || !password || !repeat) err = { ...err, fill: true };
    if (password.length < 6) err = { ...err, length: true };

    setErrors(err);

    if (!!!err /*&& recaptcha*/) {
      const data = await requests.enqueue<RecordResponse<string>>(
        new RegisterRequest({
          email,
          password,
          repeat_password: repeat,
          recaptcha: recaptchaResponse,
        })
      );
      // if (!IS_DEBUG) {
      //   reCapRefRegister.current.reset();
      // }
      if (!data) return;
      success(ok.check_email);
      changeWindow();
    }
  };

  useEffect(() => {
    const setTabIndex = (v: number, el: HTMLElement, ok = false) => {
      if (ok) {
        if (v < 0) el.tabIndex = v;
        else if (
          el instanceof HTMLInputElement ||
          el instanceof HTMLButtonElement
        )
          el.tabIndex = v;
      }
      for (const child of el.children) {
        if (child instanceof HTMLElement) setTabIndex(v, child, true);
      }
    };

    if (isSign) {
      if (registrationRef.current) setTabIndex(0, registrationRef.current);
      if (loginRef.current) setTabIndex(-1, loginRef.current);
    } else {
      if (registrationRef.current) setTabIndex(-1, registrationRef.current);
      if (loginRef.current) setTabIndex(0, loginRef.current);
    }
  }, [isSign]);

  return {
    isMob,
    recaptchaResponse,
    terms,
    isSign,
    errors,
    loading: requests.isLoading,

    reCapRefLogin,
    reCapRefRegister,

    registrationRef,
    loginRef,

    changeTerms,
    login: authorization,
    create: registration,
    openForgot: () => dispatch(show(MODALS_ENUM.FORGOT)),
    openSignup: () => {
      navigate(ROUTES_E.SIGN);
    },
    changeWindow,
    setRecaptchaResponse,
  };
};
